import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { getVapidKey, pushSubscribe, pushUnsubscribe } from '../lib/api';
import { toast } from 'sonner';

// Convert base64 URL string to Uint8Array for PushManager
const urlB64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
};

const supported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

const EnableNotifications = () => {
  const [state, setState] = useState('unknown'); // unknown | denied | on | off
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported()) { setState('unsupported'); return; }
    (async () => {
      if (Notification.permission === 'denied') { setState('denied'); return; }
      const reg = await navigator.serviceWorker.ready.catch(() => null);
      if (!reg) { setState('off'); return; }
      const sub = await reg.pushManager.getSubscription();
      setState(sub ? 'on' : 'off');
    })();
  }, []);

  if (state === 'unsupported' || state === 'unknown') return null;

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast.error('Notifications blocked in browser'); setState('denied'); return; }
      const { public_key } = await getVapidKey();
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(public_key) });
      }
      const raw = sub.toJSON();
      await pushSubscribe({ endpoint: raw.endpoint, keys: raw.keys, timezone_offset_minutes: new Date().getTimezoneOffset(), reminder_hour_local: 20 });
      setState('on'); toast.success('You’ll get a gentle nudge at 8pm if you haven’t logged.');
    } catch (e) { toast.error('Could not enable notifications'); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await pushUnsubscribe({ endpoint: sub.endpoint }); await sub.unsubscribe(); }
      setState('off'); toast.success('Reminders turned off');
    } catch (e) { toast.error('Failed to disable'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 rounded-2xl border-emerald-950/5 bg-white flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl grid place-items-center ${state==='on' ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-50 text-emerald-400'}`}>
          {state==='on' ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
        </div>
        <div>
          <p className="font-display font-bold text-emerald-950">{state==='on' ? '8pm streak reminder is on' : state==='denied' ? 'Reminders blocked' : 'Protect your streak'}</p>
          <p className="text-emerald-950/60 text-sm">{state==='denied' ? 'Re-enable notifications in your browser settings.' : state==='on' ? 'We’ll only notify you if you haven’t logged that day.' : 'A gentle nudge at 8pm if you forget to log — keeps your streak alive.'}</p>
        </div>
      </div>
      {state !== 'denied' && (
        state === 'on' ? (
          <Button variant="outline" className="rounded-full border-emerald-950/10" onClick={disable} disabled={busy}>{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Turn off</Button>
        ) : (
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" onClick={enable} disabled={busy}>{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enable</Button>
        )
      )}
    </Card>
  );
};

export default EnableNotifications;
